import React, { useEffect, useRef, useState } from 'react';

import { EMPTY_SCOREBOARD, TEAMS, clock } from '../scoreboard';
import useCountdown from '../useCountdown';

import './Scoreboard.css';

const FLASH_MS = 900; // how long a score stands out after it changes; matches the animation in the CSS
const MIN_DIGITS = 2;

// Each digit as the LEDs that light up, on a 5 × 7 matrix like the ones of a stadium board
const GLYPHS = {
  0: ['.###.', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  1: ['..#..', '.##..', '..#..', '..#..', '..#..', '..#..', '.###.'],
  2: ['.###.', '#...#', '....#', '...#.', '..#..', '.#...', '#####'],
  3: ['.###.', '#...#', '....#', '..##.', '....#', '#...#', '.###.'],
  4: ['...#.', '..##.', '.#.#.', '#..#.', '#####', '...#.', '...#.'],
  5: ['#####', '#....', '####.', '....#', '....#', '#...#', '.###.'],
  6: ['.###.', '#...#', '#....', '####.', '#...#', '#...#', '.###.'],
  7: ['#####', '....#', '...#.', '..#..', '.#...', '.#...', '.#...'],
  8: ['.###.', '#...#', '#...#', '.###.', '#...#', '#...#', '.###.'],
  9: ['.###.', '#...#', '#...#', '.####', '....#', '#...#', '.###.'],
};
const BLANK = Array(7).fill('#####'); // every LED of the matrix, for the unlit ones behind a digit

// A digit is three paths, however many LEDs it lights: the unlit matrix, the glow and the LEDs
const LED_RADIUS = 0.36;
const GLOW_RADIUS = 0.5;
const dots = (rows, radius) =>
  rows
    .flatMap((row, y) => [...row].map((cell, x) => (cell === '#' ? [x + 0.5, y + 0.5] : null)))
    .filter(Boolean)
    .map(([cx, cy]) => `M${(cx - radius).toFixed(2)} ${cy}a${radius} ${radius} 0 1 0 ${radius * 2} 0a${radius} ${radius} 0 1 0 ${-radius * 2} 0`)
    .join('');

const UNLIT = dots(BLANK, LED_RADIUS);
const LIT = Object.fromEntries(
  Object.entries(GLYPHS).map(([digit, rows]) => [digit, { leds: dots(rows, LED_RADIUS), glow: dots(rows, GLOW_RADIUS) }])
);

const Digit = React.memo(({ value }) => (
  <svg className='led' viewBox='-0.5 -0.5 6 8' aria-hidden='true' focusable='false'>
    <path className='led__off' d={UNLIT} />
    <path className='led__glow' d={LIT[value]?.glow} />
    <path className='led__on' d={LIT[value]?.leds} />
  </svg>
));

// A number on the board: drawn with LEDs, and said in words for whoever cannot see them
export const Leds = ({ text, label, role = 'img', className = '' }) => (
  <span className={`leds ${className}`} role={role} aria-label={label}>
    {[...text].map((char, at) =>
      char === ':' ? <span key={at} className='leds__colon' /> : <Digit key={at} value={char} />
    )}
  </span>
);

// A score stands out for a moment when it changes live; never when it just loaded
const useFlash = (value, live) => {
  const [flash, setFlash] = useState(false);
  const previous = useRef(value);

  useEffect(() => {
    if (previous.current === value) return undefined;
    previous.current = value;
    if (!live) return undefined;
    setFlash(true);
    const timer = setTimeout(() => setFlash(false), FLASH_MS);
    return () => clearTimeout(timer);
  }, [value, live]);

  return flash;
};

const Score = ({ label, value, digits, live }) => {
  const flash = useFlash(value, live);
  return (
    <Leds
      text={String(value).padStart(digits, '0')}
      label={`${label}: ${value} puntos`}
      className={`scoreboard__score${flash ? ' scoreboard__score--flash' : ''}`}
    />
  );
};

/**
 * The scoreboard of the team games, like the ones over a football field: both teams, each in
 * its colour, their points in LEDs and the time left of the game being played. The admin
 * panel gives the points and runs the timer; every phone and the screen of the event show
 * the same.
 */
const Scoreboard = ({ scoreboard = EMPTY_SCOREBOARD }) => {
  const { seconds, over } = useCountdown(scoreboard.timer);

  // Both teams with as many digits as the one ahead needs, so the board stays symmetric
  const digits = Math.max(MIN_DIGITS, ...TEAMS.map(({ id }) => String(scoreboard[id]).length));

  return (
    <div className={`scoreboard${digits > MIN_DIGITS ? ' scoreboard--wide' : ''}`} role='group' aria-label='Marcador'>
      <div className='scoreboard__teams' aria-hidden='true'>
        {TEAMS.map(({ id, label }) => (
          <span key={id} className={`scoreboard__team scoreboard__team--${id}`}>
            {label}
          </span>
        ))}
      </div>

      {/* Each score under the name of its team, and the two white squares of a board between them */}
      <div className='scoreboard__scores'>
        {TEAMS.map(({ id, label }, at) => (
          <React.Fragment key={id}>
            {at > 0 && <span className='scoreboard__versus' aria-hidden='true' />}
            <Score label={label} value={scoreboard[id]} digits={digits} live={scoreboard.live} />
          </React.Fragment>
        ))}
      </div>

      <div className='scoreboard__time'>
        <span className='scoreboard__label' aria-hidden='true'>
          Tiempo
        </span>
        <Leds
          text={clock(seconds)}
          label={`Tiempo: ${clock(seconds)}`}
          role='timer'
          className={`scoreboard__clock${over ? ' scoreboard__clock--over' : ''}`}
        />
        <span className='scoreboard__label' aria-hidden='true'>
          Tiempo
        </span>
      </div>
    </div>
  );
};

export default Scoreboard;
